import { useMediaQuery } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import styles from "../../../styles/SubscribeThematics.module.scss";
import cartStyles from "../../../styles/SubscribeCart.module.scss";
import useFragments from "../../../hooks/useFragments";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { LoadableText } from "../../UI/LoadableText";
import { getStripeProductFromDiseases, formatStripeProduct } from "../../../utils/liberals";
import { pluralize } from "../../../utils/utils";
import { STRIPE_PRODUCTS_LS_ID } from "../constants";

/** CartSection component, responsible for displaying the cart section of the subscribe page.
 *
 * This uses some sort of state to store the selected product and thematics between auth "pages",
 * but also between the WP page to choose Nuvee pack and the auth page to choose the thematics.
 *
 * @param {Array<string>} selectedThematics - The currently selected thematics
 * @param {Function} goToCart - The function to call when the user wants to go to the cart
 */
export default function CartSection({ selectedThematics = [], goToCart = () => {} }) {
  const apIUrl = (path = "") => `${process.env.REACT_APP_NUVEE_BASE_URL ?? ""}${path}`;
  const isMobile = useMediaQuery("(max-width: 768px)");

  /** @typedef { [key: string]: { id: string, name: string, localized_price: string, NbThematics: number, NbPatients: number } } Products */
  const [products, setProducts] = useState({});
  const [selectedProduct, setSelectedProduct] = useState({});
  const [_, setLocalStorageProducts] = useLocalStorage(STRIPE_PRODUCTS_LS_ID, []);

  /** This component uses URl fragmentsstateInfos to retrieve the product selected from Nuvee WP
   * to select the correct product in the products state.
   *  * Example usage in url:
   * ```js
   * Url("#product=prod_RPOP09DNHxiOsZ") // Select the Stripe product with id prod_RPOP09DNHxiOsZ
   * ```
   */
  const { urlFragments: stateInfos, removeFragments } = useFragments();

  useEffect(() => {
    let isMounted = true;

    axios
      .get(apIUrl("/stripe/liberals"))
      .then(({ data }) => {
        if (!Object.keys(products).length && isMounted) {
          const reducedProducts = data.reduce((acc, product) => {
            acc[product.metadata.NbThematics] = formatStripeProduct(product);
            return acc;
          }, {});
          setProducts(reducedProducts);
          setLocalStorageProducts(reducedProducts);
        }
      })
      .catch(console.error);

    return () => {
      isMounted = false;
    };
  }, []);

  /** Get the current selected product key for the `products` state */
  const currentSelectedProductKey =
    Object.keys(products).length > 0 ? Object.entries(products).find(([_, product]) => product.id === selectedProduct.id)?.[0] ?? 0 : -1;

  /** Initiate the selected product, or select the highest product from the selected thematics */
  useEffect(() => {
    if (currentSelectedProductKey !== -1) {
      const foundProduct = getStripeProductFromDiseases(products, selectedThematics, selectedProduct);
      if (foundProduct) setSelectedProduct(foundProduct);
    }
  }, [selectedThematics, currentSelectedProductKey]);

  useEffect(() => {
    // Wait for selectedProduct to be set to replace it with hash if it exists
    if (stateInfos.product && Object.keys(products) && selectedProduct?.id) {
      setSelectedProduct(Object.values(products).find((p) => p.id === stateInfos.product) ?? {});
      removeFragments(["product"]);
    }
  }, [selectedProduct, stateInfos, products]);

  // Check if the features list should be displayed (or display a skeleton)
  const featuresListTrigger = !isNaN(selectedProduct.NbThematics) && !isNaN(selectedProduct.NbPatients);

  return (
    <div id="cart-section" className={styles.cart}>
      <div className={cartStyles.cartRecap}>
        <p className={cartStyles.title}>Votre choix</p>
        <span className={cartStyles.divider} />

        <div className={cartStyles.price}>
          <p className={cartStyles.titleText}>{<LoadableText text={selectedProduct?.name} />}</p>
          <p className={cartStyles.priceText}>{<LoadableText text={selectedProduct?.localized_price} />}</p>
          <p className={cartStyles.subtitleText}>(paiement mensuel)</p>
        </div>
        <span className={cartStyles.divider} />

        <ul className={cartStyles.features}>
          <li>
            <LoadableText
              text={`${selectedProduct.NbThematics} ${pluralize("thématique", selectedProduct.NbThematics)} Nuvee au choix`}
              trigger={featuresListTrigger}
            />
          </li>
          <li>
            <LoadableText text={`${selectedProduct.NbPatients} accès patients crédités par mois`} trigger={featuresListTrigger} />
          </li>
          <li>
            {selectedProduct.NbThematics < 2 ? <LoadableText text={"Jusqu'à 2 parcours pédagogiques"} trigger={featuresListTrigger} /> : <LoadableText text={"Jusqu'à 4 parcours pédagogiques"} trigger={featuresListTrigger} />}
          </li>
          <li>
            <LoadableText text={`Sans engagement`} trigger={featuresListTrigger} />
          </li>
        </ul>
      </div>

      <div className={styles.cartActions}>
        <button disabled={selectedThematics.length === 0} onClick={goToCart}>
          {isMobile ? "SUIVANT" : "COMMANDER"}
        </button>
        <p>
          * L’offre {selectedProduct?.name?.toUpperCase() ?? "DÉCOUVERTE"} vous donne accès à {selectedProduct.NbThematics ?? 1}{" "}
          {pluralize("thématique", selectedProduct.NbThematics ?? 1)}.{" "}
          {selectedProduct?.name?.toUpperCase() === "AVANCÉE" ? 
            "Pour revenir à l'offre précédente, désélectionnez une thématique." 
            :
            "Pour passer à l'offre supérieure, ajoutez une thématique"
          }
        </p>
      </div>
    </div>
  );
}
